import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { htmlToText, resolveStoreName } from '@/lib/display';
import type { ThemeBlock, ThemeBlockSchema } from '@/lib/themeBlocks';

import ProductsTag from '@/components/feature/template-tags/ProductsTag';
import ServicesTag from '@/components/feature/template-tags/ServicesTag';
import BlogPostsTag from '@/components/feature/template-tags/BlogPostsTag';
import PartnersTag from '@/components/feature/template-tags/PartnersTag';
import TeamTag from '@/components/feature/template-tags/TeamTag';
import PortfolioTag from '@/components/feature/template-tags/PortfolioTag';
import ContactFormTag from '@/components/feature/template-tags/ContactFormTag';
import BookingFormTag from '@/components/feature/template-tags/BookingFormTag';
import TestimonialsTag from '@/components/feature/template-tags/TestimonialsTag';
import StatsTag from '@/components/feature/template-tags/StatsTag';
import MarketplaceProductsTag from '@/components/feature/template-tags/MarketplaceProductsTag';
import MarketplaceSellersTag from '@/components/feature/template-tags/MarketplaceSellersTag';
import MarketplaceSearchTag from '@/components/feature/template-tags/MarketplaceSearchTag';
import MarketplaceCategoriesTag from '@/components/feature/template-tags/MarketplaceCategoriesTag';
import FeaturedProductsTag from '@/components/feature/template-tags/FeaturedProductsTag';
import FaqTag from '@/components/feature/template-tags/FaqTag';
import GalleryTag from '@/components/feature/template-tags/GalleryTag';
import HebergementsTag from '@/components/feature/template-tags/HebergementsTag';
import HebergementSearchTag from '@/components/feature/template-tags/HebergementSearchTag';
import EventsTag from '@/components/feature/template-tags/EventsTag';
import FormationsTag from '@/components/feature/template-tags/FormationsTag';
import CategoriesTag from '@/components/feature/template-tags/CategoriesTag';
import RestaurationTag from '@/components/feature/template-tags/RestaurationTag';
import DefaultFormTag from '@/components/feature/template-tags/DefaultFormTag';
import HeroBannersTag from '@/components/feature/template-tags/HeroBannersTag';
import TabsBlock, { type TabItem } from '@/components/feature/theme-blocks/TabsBlock';
import BeforeAfterBlock from '@/components/feature/theme-blocks/BeforeAfterBlock';
import TrustBannerBlock, { type TrustItem } from '@/components/feature/theme-blocks/TrustBannerBlock';

// ============================================================
// Moteur de rendu « React » : transforme un schéma de blocs (JSON)
// en COMPOSANTS React réels affichés sur le site public.
// Les blocs dynamiques réutilisent les composants du moteur de
// balises Zifek (produits, services, formulaires…), donc le rendu
// reste identique et parfaitement intégré aux pages publiques.
// ============================================================

type Props = Record<string, unknown>;

function str(value: unknown, fallback = ''): string {
  return value === undefined || value === null ? fallback : String(value);
}

function num(value: unknown, fallback?: number): number | undefined {
  const n = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);
  return Number.isNaN(n) ? fallback : n;
}

interface TagContext {
  site_name: string;
  site_description: string;
  site_logo: string;
  site_phone: string;
  site_email: string;
  site_address: string;
  site_about: string;
  site_url: string;
  year: string;
}

/** Remplace les balises Zifek simples dans un texte statique (hero, CTA…). */
function resolveInline(value: string, ctx: TagContext): string {
  return value.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, name: string) => {
    switch (name) {
      case 'site_name': return ctx.site_name;
      case 'site_description': return ctx.site_description;
      case 'site_phone': return ctx.site_phone;
      case 'site_email': return ctx.site_email;
      case 'site_address': return ctx.site_address;
      case 'site_about': return ctx.site_about;
      case 'site_url': return ctx.site_url;
      case 'year':
      case 'current_year': return ctx.year;
      case 'site_logo': return ctx.site_logo;
      default: return '';
    }
  });
}

function alignClass(align: unknown): string {
  const a = str(align, 'left');
  if (a === 'center') return 'items-center text-center';
  if (a === 'right') return 'items-end text-right';
  return 'items-start text-left';
}

/** Récupère un tableau d'objets depuis une prop, en ignorant tout élément invalide. */
function asArrayOfObjects(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object');
}

function BlockShell({ children }: { children: ReactNode }) {
  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">{children}</div>
    </div>
  );
}

export default function ThemeBlockRenderer({ schema }: { schema: ThemeBlockSchema }) {
  const { tenant, theme } = useTenant();

  const ctx = useMemo<TagContext>(() => ({
    site_name: resolveStoreName(tenant, theme),
    site_description: htmlToText(tenant?.description) || '',
    site_logo: tenant?.image || theme?.navImage || '',
    site_phone: tenant?.telephone || '',
    site_email: tenant?.email || '',
    site_address: [tenant?.adresse, tenant?.Quartier, tenant?.Ville, tenant?.Pays].filter(Boolean).join(', '),
    site_about: htmlToText(tenant?.aboutus) || htmlToText(tenant?.description),
    site_url: typeof window !== 'undefined' ? window.location.origin : '',
    year: String(new Date().getFullYear()),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tenant, theme]);

  const renderBlock = (block: ThemeBlock, index: number): ReactNode => {
    const p: Props = block.props || {};
    const key = `${block.type}-${index}`;

    switch (block.type) {
      /* ── Structure ────────────────────────────────────────── */
      case 'hero': {
        const title = resolveInline(str(p.title, '{{site_name}}'), ctx);
        const subtitle = resolveInline(str(p.subtitle, '{{site_description}}'), ctx);
        const image = str(p.image);
        return (
          <section key={key} className="relative w-full overflow-hidden bg-foreground-950">
            {image ? (
              <img src={image} alt={title} className="absolute inset-0 h-full w-full object-cover object-top opacity-60" />
            ) : null}
            <div className={`absolute inset-0 ${image ? 'bg-black/45' : 'bg-gradient-to-br from-primary-600 to-foreground-950'}`}></div>
            <div className="relative mx-auto flex w-full max-w-6xl flex-col items-center gap-4 px-4 py-24 text-center md:px-6 md:py-32">
              <h1 className="font-heading text-3xl font-bold text-background-50 md:text-5xl">{title}</h1>
              {subtitle ? <p className="max-w-2xl text-sm text-background-100/90 md:text-base">{subtitle}</p> : null}
              <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
                {p.ctaLabel ? (
                  <Link to={str(p.ctaHref, '/')} className="rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
                    {resolveInline(str(p.ctaLabel), ctx)}
                  </Link>
                ) : null}
                {p.cta2Label ? (
                  <Link to={str(p.cta2Href, '/')} className="rounded-full border border-background-50/60 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10">
                    {resolveInline(str(p.cta2Label), ctx)}
                  </Link>
                ) : null}
              </div>
            </div>
          </section>
        );
      }
      case 'cta':
        return (
          <section key={key} className="py-14">
            <BlockShell>
              <div className="flex flex-col items-center gap-4 rounded-2xl bg-accent-500 px-6 py-12 text-center">
                <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">{resolveInline(str(p.title), ctx)}</h2>
                {p.text ? <p className="max-w-xl text-sm text-background-100">{resolveInline(str(p.text), ctx)}</p> : null}
                {p.buttonLabel ? (
                  <Link to={str(p.buttonHref, '/contact')} className="mt-2 rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-950 transition-opacity hover:opacity-90">
                    {resolveInline(str(p.buttonLabel), ctx)}
                  </Link>
                ) : null}
              </div>
            </BlockShell>
          </section>
        );
      case 'spacer':
        return <div key={key} style={{ height: `${num(p.height, 48)}px` }} aria-hidden="true" />;
      case 'divider':
        return (
          <div key={key} className="py-6">
            <BlockShell><hr className="border-t border-background-200/70" /></BlockShell>
          </div>
        );

      /* ── Contenu ──────────────────────────────────────────── */
      case 'heading': {
        const level = num(p.level, 2) || 2;
        const text = resolveInline(str(p.text), ctx);
        const cls = `font-heading font-bold text-foreground-950 ${level <= 1 ? 'text-3xl md:text-4xl' : level === 2 ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'}`;
        return (
          <div key={key} className="pt-10">
            <BlockShell>
              <div className={`flex flex-col ${alignClass(p.align)}`}>
                <h2 className={cls}>{text}</h2>
              </div>
            </BlockShell>
          </div>
        );
      }
      case 'text': {
        const html = resolveInline(str(p.html), ctx);
        return (
          <div key={key} className="py-4">
            <BlockShell>
              <div
                className={`flex flex-col gap-2 text-sm leading-relaxed text-foreground-700 md:text-base ${alignClass(p.align)}`}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </BlockShell>
          </div>
        );
      }
      case 'image': {
        const src = str(p.src);
        return (
          <div key={key} className="py-4">
            <BlockShell>
              {src ? (
                <img src={src} alt={str(p.alt)} style={{ borderRadius: `${num(p.radius, 16)}px` }} className="w-full object-cover object-top" />
              ) : (
                <div className="flex h-48 w-full items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
                  <i className="ri-image-line text-3xl"></i>
                </div>
              )}
            </BlockShell>
          </div>
        );
      }
      case 'tabs': {
        const items: TabItem[] = asArrayOfObjects(p.items).map((it) => ({
          title: resolveInline(str(it.title), ctx),
          html: resolveInline(str(it.html), ctx),
        }));
        return <div key={key} className="py-8"><BlockShell><TabsBlock items={items} /></BlockShell></div>;
      }
      case 'before_after':
        return (
          <div key={key} className="py-8">
            <BlockShell>
              <BeforeAfterBlock
                imageBefore={str(p.imageBefore)}
                imageAfter={str(p.imageAfter)}
                labelBefore={resolveInline(str(p.labelBefore, 'Avant'), ctx)}
                labelAfter={resolveInline(str(p.labelAfter, 'Après'), ctx)}
              />
            </BlockShell>
          </div>
        );
      case 'trust_banner': {
        const items: TrustItem[] = asArrayOfObjects(p.items).map((it) => ({
          icon: str(it.icon, 'ri-checkbox-circle-line'),
          label: resolveInline(str(it.label), ctx),
        }));
        return <div key={key} className="py-8"><BlockShell><TrustBannerBlock items={items} /></BlockShell></div>;
      }

      /* ── Dynamique (composants du moteur de balises) ──────── */
      case 'products': return <div key={key} className="py-8"><BlockShell><ProductsTag limit={num(p.limit)} /></BlockShell></div>;
      case 'featured_products': return <div key={key} className="py-8"><BlockShell><FeaturedProductsTag limit={num(p.limit, 6) ?? 6} /></BlockShell></div>;
      case 'services': return <div key={key} className="py-8"><BlockShell><ServicesTag /></BlockShell></div>;
      case 'testimonials': return <div key={key} className="py-8"><BlockShell><TestimonialsTag /></BlockShell></div>;
      case 'stats': return <div key={key} className="py-8"><BlockShell><StatsTag /></BlockShell></div>;
      case 'faq': return <div key={key} className="py-8"><BlockShell><FaqTag /></BlockShell></div>;
      case 'gallery': return <div key={key} className="py-8"><BlockShell><GalleryTag limit={num(p.limit)} /></BlockShell></div>;
      case 'blog_posts': return <div key={key} className="py-8"><BlockShell><BlogPostsTag /></BlockShell></div>;
      case 'partners': return <div key={key} className="py-8"><BlockShell><PartnersTag /></BlockShell></div>;
      case 'team': return <div key={key} className="py-8"><BlockShell><TeamTag /></BlockShell></div>;
      case 'portfolio': return <div key={key} className="py-8"><BlockShell><PortfolioTag /></BlockShell></div>;
      case 'hebergements': return <div key={key} className="py-8"><BlockShell><HebergementsTag limit={num(p.limit)} /></BlockShell></div>;
      case 'hebergement_search': return <div key={key} className="py-8"><BlockShell><HebergementSearchTag limit={num(p.limit, 6)} /></BlockShell></div>;
      case 'events': return <div key={key} className="py-8"><BlockShell><EventsTag limit={num(p.limit)} /></BlockShell></div>;
      case 'formations': return <div key={key} className="py-8"><BlockShell><FormationsTag limit={num(p.limit)} /></BlockShell></div>;
      case 'categories': return <div key={key} className="py-8"><BlockShell><CategoriesTag /></BlockShell></div>;
      case 'restauration': return <div key={key} className="py-8"><BlockShell><RestaurationTag limit={num(p.limit, 8)} /></BlockShell></div>;
      case 'default_form': return <div key={key} className="py-8"><BlockShell><DefaultFormTag /></BlockShell></div>;
      case 'hero_banners': return <div key={key} className="py-8"><BlockShell><HeroBannersTag /></BlockShell></div>;
      case 'contact_form': return <div key={key} className="py-8"><BlockShell><ContactFormTag /></BlockShell></div>;
      case 'booking_form': return <div key={key} className="py-8"><BlockShell><BookingFormTag /></BlockShell></div>;
      case 'marketplace_products': return <div key={key} className="py-8"><BlockShell><MarketplaceProductsTag limit={num(p.limit)} /></BlockShell></div>;
      case 'marketplace_sellers': return <div key={key} className="py-8"><BlockShell><MarketplaceSellersTag limit={num(p.limit)} /></BlockShell></div>;
      case 'marketplace_search': return <div key={key} className="py-8"><BlockShell><MarketplaceSearchTag placeholder={str(p.placeholder) || undefined} /></BlockShell></div>;
      case 'marketplace_categories': return <div key={key} className="py-8"><BlockShell><MarketplaceCategoriesTag /></BlockShell></div>;
      default: return null;
    }
  };

  return (
    <div className="zf-react-theme w-full">
      {schema.blocks.map((block, i) => renderBlock(block, i))}
    </div>
  );
}